import {useEffect, useRef} from 'react'
import {useResizable} from 'react-resizable-layout'
import {Alert, Snackbar} from '@mui/material'
import PropTypes from 'prop-types'
import DragBar from './DragBar'
import ConfirmationDialog from './ConfirmationDialog'
import {useMsal} from '@azure/msal-react'
import useFetchInterceptor from '../helpers/useFetchInterceptor'
import {getFormattedAIInfoText, getParsedData, getRandomId, getSessionData, setSessionData} from './helpers'
import {getMinWidth, getMaxWidth} from './utils/chatUtils'
import {usePermissions} from './hooks/usePermissions'
import {useChatState} from './hooks/useChatState'
import useMessageHandlers from './hooks/useMessageHandlers'
import useEventHandlers from './hooks/useEventHandlers'
import useSessionManagement from './hooks/useSessionManagement'
import useExportHandlers from './hooks/useExportHandlers'
import useMessageProcessor from './hooks/useMessageProcessor'
import useRequestManager from './hooks/useRequestManager'
import useLayoutUtilities from './hooks/useLayoutUtilities'
import {
  StyledChatWindowContainer,
  StyledChatWindow,
  StyledChatWindowSubContainer,
  StyledBodyContainer
} from './ChatbotStyles'
import {useCustomDashboardViewProvider} from '../../store/hooks/customDashboardView'
import ChatHeader from './common/ChatHeader'
import WarningBanner from './common/WarningBanner'
import ChatInput from './common/ChatInput'
import DownloadMenus from './common/DownloadMenus'
import MessageList from './common/MessageList'

const ChatWindow = ({handleClose, showChatWindow, permission, admin}) => {
  const {accounts, instance, inProgress} = useMsal()
  const sendButtonRef = useRef(null)
  const messageInputRef = useRef(null)
  const csvFileRef = useRef(null)
  const chatBodyScrollContainerRef = useRef(null)
  const chatFooterContainerRef = useRef(null)
  const {prismProgram} = useCustomDashboardViewProvider()

  const {isDragging, position, separatorProps, setPosition} = useResizable({
    axis: 'x',
    min: getMinWidth(),
    max: getMaxWidth(),
    reverse: true
  })

  // Consolidated state management
  const {uiState, chatState, sessionState, menuState, notificationState} = useChatState(position)

  // Destructure commonly used state variables for easier access
  const {
    minWidth,
    setMinWidth,
    maxWidth,
    setMaxWidth,
    showFullScreen,
    setShowFullScreen,
    showDragbar,
    setShowDragbar,
    containerWidth,
    setContainerWidth,
    hiddenWarningBanners,
    setHiddenWarningBanners
  } = uiState

  const {
    messages,
    setMessages,
    showLoader,
    setShowLoader,
    disableSendButton,
    setDisableSendButton,
    isRequestActive,
    setIsRequestActive,
    activeRequestId,
    setActiveRequestId
  } = chatState

  const {userId, setUserId, sessionId, setSessionId, isInitialized, setIsInitialized} = sessionState

  const {
    chartDownloadMenuAnchor,
    setChartDownloadMenuAnchor,
    selectedChartMessageId,
    setSelectedChartMessageId,
    downloadMenuAnchor,
    setDownloadMenuAnchor,
    selectedDownloadMessageId,
    setSelectedDownloadMessageId,
    showConfirmationDialog,
    setShowConfirmationDialog,
    expandShrinkTooltipOpen,
    setExpandShrinkTooltipOpen
  } = menuState

  const {showCopyToClipboard, setShowCopyToClipboard} = notificationState

  // Permission checks for chatbot functionality
  const {canExport, canCopy} = usePermissions(permission, admin)
  const title = `${prismProgram} Agent is currently available in "Beta" Phase. Our team is actively working to enhance its robustness and user experience.`

  const {post} = useFetchInterceptor({
    baseURL: globalThis.__env.REACT_APP_AGENTS_API_URL
  })

  // Layout utilities hook for window resizing and scroll handling
  const {
    resetSizes,
    adjustChatBodyMaxHeight,
    debouncedHandleScroll,
    clearAllTimeouts,
    autoScrollTimeoutRef,
    autoScrollToMessageTimeoutRef
  } = useLayoutUtilities({showFullScreen}, {setMaxWidth, setMinWidth, setContainerWidth}, {getMinWidth, getMaxWidth})

  // Test useMessageHandlers hook with one function
  const {
    clearMessageInput: clearMessageInputFromHook,
    handleAutoScrollToBottom: handleAutoScrollToBottomFromHook,
    handleExpandShrink: handleExpandShrinkFromHook,
    handleBannerClose: handleBannerCloseFromHook,
    handleAutoScrollToMessage: handleAutoScrollToMessageFromHook
  } = useMessageHandlers(
    {messages, sessionId, userId, isInitialized, showFullScreen, hiddenWarningBanners},
    {
      setMessages,
      setDisableSendButton,
      setIsRequestActive,
      setContainerWidth,
      setPosition,
      setShowDragbar,
      setShowFullScreen,
      setExpandShrinkTooltipOpen,
      setHiddenWarningBanners
    },
    {messageInputRef, sendButtonRef, autoScrollTimeoutRef, chatBodyScrollContainerRef, autoScrollToMessageTimeoutRef},
    {resetSizes, getMinWidth, getMaxWidth}
  )

  const handleToggle = (msgObj, type) => {
    if (type === 'SourceCode') {
      const updatedMessages = messages.map((msg) =>
        msg.id === msgObj.id ? {...msg, showSourceCode: !msg.showSourceCode} : msg
      )
      setMessages(updatedMessages)
      handleAutoScrollToMessageFromHook(msgObj)
    }
  }

  // Session management hook
  const {
    initializeSession: initializeSessionFromHook,
    populateGuidingPromptsIfReq: populateGuidingPromptsIfReqFromHook,
    handleClearHistory: handleClearHistoryFromHook
  } = useSessionManagement(
    {accounts, prismProgram, messages, isInitialized, sessionId},
    {setSessionId, setIsInitialized, setMessages},
    {post}
  )

  // Export handlers hook
  const {
    handleExportChatHistory: handleExportChatHistoryFromHook,
    handleChartDownloadMenuOpen: handleChartDownloadMenuOpenFromHook,
    handleChartDownloadMenuClose: handleChartDownloadMenuCloseFromHook,
    handleChartDownloadSelection: handleChartDownloadSelectionFromHook,
    handleDownloadMenuOpen: handleDownloadMenuOpenFromHook,
    handleDownloadMenuClose: handleDownloadMenuCloseFromHook,
    handleDownloadSelection: handleDownloadSelectionFromHook
  } = useExportHandlers(
    {messages, accounts, prismProgram},
    {setChartDownloadMenuAnchor, setSelectedChartMessageId, setDownloadMenuAnchor, setSelectedDownloadMessageId}
  )

  // Request manager hook
  const {
    showChatLoader: showChatLoaderFromHook,
    hideChatLoader: hideChatLoaderFromHook,
    populateErrorResponse: populateErrorResponseFromHook,
    handleCancelRequest: handleCancelRequestFromHook
  } = useRequestManager(
    {activeRequestId, prismProgram},
    {setActiveRequestId, setIsRequestActive, setShowLoader, setMessages},
    {post, handleAutoScrollToBottom: handleAutoScrollToBottomFromHook}
  )

  // Event handlers hook for large functions
  const {handleQuerySubmit: handleQuerySubmitFromHook, abortCurrentRequest} = useEventHandlers(
    {userId, sessionId, isInitialized, prismProgram, activeRequestId},
    {setMessages, setIsRequestActive, setActiveRequestId},
    {
      initializeSession: initializeSessionFromHook,
      hideChatLoader: hideChatLoaderFromHook,
      populateErrorResponse: populateErrorResponseFromHook,
      handleAutoScrollToBottomFromHook,
      post
    }
  )

  // Message processor hook
  const {
    handlePromptClick: handlePromptClickFromHook,
    handleSend: handleSendFromHook,
    handleCopy: handleCopyFromHook,
    handleUserMessageKeyDown: handleUserMessageKeyDownFromHook
  } = useMessageProcessor(
    {messages, messageInputRef},
    {setMessages, setShowCopyToClipboard},
    {
      showChatLoader: showChatLoaderFromHook,
      handleAutoScrollToBottom: handleAutoScrollToBottomFromHook,
      handleQuerySubmit: handleQuerySubmitFromHook,
      clearMessageInput: clearMessageInputFromHook
    }
  )

  // Create aliases for hook functions to maintain compatibility
  const handleSend = handleSendFromHook

  // Combined cancel request handler that calls both abort and cleanup functions
  const handleCancelRequest = () => {
    if (abortCurrentRequest) {
      abortCurrentRequest()
    }
    handleCancelRequestFromHook()
  }
  const handlePromptClick = handlePromptClickFromHook
  const handleCopy = handleCopyFromHook
  const handleUserMessageKeyDown = handleUserMessageKeyDownFromHook

  // Extended cleanup function that includes menu state
  const handleComponentCleanup = () => {
    clearAllTimeouts()
    // Clean up menu state
    setChartDownloadMenuAnchor(null)
    setSelectedChartMessageId(null)
    setDownloadMenuAnchor(null)
    setSelectedDownloadMessageId(null)
  }

  // Component mount: Initialize session data and setup
  useEffect(() => {
    // Restore messages from session storage
    const cachedMessages = getSessionData('messages')
    if (cachedMessages) {
      const parsedMessages = getParsedData(cachedMessages)
      if (parsedMessages && Array.isArray(parsedMessages) && parsedMessages.length > 0) {
        // Clean up any incomplete/streaming messages from previous session
        const cleanedMessages = parsedMessages
          .map((msg) => {
            if (msg.isStreaming) {
              // Remove streaming state and mark as completed
              return {
                ...msg,
                isStreaming: false,
                responseText: msg.responseText || 'Message was interrupted during page refresh.'
              }
            }
            return msg
          })
          .filter((msg) => {
            // Remove any messages that are completely empty or invalid
            return msg.message || msg.responseText || msg.graphData || msg.plotlyJson
          })

        setMessages(cleanedMessages)
      } else {
        populateGuidingPromptsIfReqFromHook()
      }
    } else {
      populateGuidingPromptsIfReqFromHook()
    }

    // Restore session state
    const cachedSessionId = getSessionData('currentSessionId')
    const cachedUserId = getSessionData('currentUserId')

    if (cachedSessionId) {
      const parsedSessionId = getParsedData(cachedSessionId)
      if (parsedSessionId) {
        setSessionId(parsedSessionId)
        setIsInitialized(true)
      }
    }
    if (cachedUserId) {
      const parsedUserId = getParsedData(cachedUserId)
      if (parsedUserId) {
        setUserId(parsedUserId)
      }
    }

    // Initialize default sessions if needed
    const cachedSessions = getSessionData('sessions')
    if (cachedSessions == null) {
      setSessionData('sessions', [getRandomId('s1-'), getRandomId('s2-')])
    }

    // Restore warning banner preferences
    const userAckWarningBanner = getSessionData('userAckWarningBanner')
    if (userAckWarningBanner) {
      setHiddenWarningBanners(getParsedData(userAckWarningBanner))
    }

    // Setup resize observers
    resetSizes()
    adjustChatBodyMaxHeight(chatBodyScrollContainerRef, chatFooterContainerRef)
    const observer = new ResizeObserver(resetSizes)
    observer.observe(document.body)
    const footerObserver = new ResizeObserver(() =>
      adjustChatBodyMaxHeight(chatBodyScrollContainerRef, chatFooterContainerRef)
    )
    footerObserver.observe(chatFooterContainerRef.current)

    return () => {
      observer.disconnect()
      footerObserver.disconnect()
      handleComponentCleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-scroll when chat window shows
  useEffect(() => {
    handleAutoScrollToBottomFromHook('auto')
  }, [showChatWindow, handleAutoScrollToBottomFromHook])

  // Persist warning banner preferences
  useEffect(() => {
    if (hiddenWarningBanners && Array.isArray(hiddenWarningBanners) && hiddenWarningBanners.length > 0) {
      setSessionData('userAckWarningBanner', JSON.stringify(hiddenWarningBanners))
    }
  }, [hiddenWarningBanners])

  // Persist messages to session storage
  useEffect(() => {
    if (messages.length > 0) {
      setSessionData('messages', messages)
    }
  }, [messages])

  // Persist session ID and user ID
  useEffect(() => {
    if (sessionId) {
      setSessionData('currentSessionId', sessionId)
    }
  }, [sessionId])

  useEffect(() => {
    if (userId) {
      setSessionData('currentUserId', userId)
    }
  }, [userId])

  // Handle resizable position changes
  useEffect(() => {
    resetSizes()
    setContainerWidth(position)
    if (position === maxWidth) {
      setShowFullScreen(true)
    } else {
      setShowFullScreen(false)
      setShowDragbar(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position])

  // Initialize session when user authenticates
  useEffect(() => {
    if (accounts?.[0] && inProgress === 'none') {
      const userId = accounts?.[0]?.username // Extract user email from MSAL authentication
      setUserId(userId)
      initializeSessionFromHook()
    }
  }, [inProgress, accounts, instance, initializeSessionFromHook, setUserId])

  const handleKeydown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      const trimmedValue = messageInputRef.current?.value?.trim()
      if (trimmedValue !== '' && !disableSendButton && !showLoader) {
        handleSend()
      }
    }
  }

  const getAIDisclaimerText = (msgObj) => {
    return getFormattedAIInfoText(msgObj.aiInfoText)
  }

  const getUserAdditionalProps = (msgObj) => {
    const additionalProps = {
      ...(msgObj.isPrompt && {role: 'button'})
    }
    additionalProps['aria-label'] = `${msgObj.label ? msgObj.label : ''}: ${msgObj.message}`
    return additionalProps
  }

  const getBotAdditionalProps = (msgObj) => {
    const additionalProps = {}
    additionalProps['aria-label'] = `${msgObj.label ? msgObj.label : ''}: ${
      msgObj.graphData ? `Showing ${msgObj.graphData?.layout?.title?.text || ''} Chart` : msgObj.responseText
    }`
    return additionalProps
  }

  return (
    <StyledChatWindowContainer>
      {showDragbar && <DragBar isDragging={isDragging} {...separatorProps} />}
      <StyledChatWindowSubContainer>
        <StyledChatWindow width={containerWidth} minWidth={minWidth} maxWidth={maxWidth}>
          <ChatHeader
            prismProgram={prismProgram}
            title={title}
            showFullScreen={showFullScreen}
            expandShrinkTooltipOpen={expandShrinkTooltipOpen}
            setExpandShrinkTooltipOpen={setExpandShrinkTooltipOpen}
            handleExpandShrinkFromHook={handleExpandShrinkFromHook}
            setShowConfirmationDialog={setShowConfirmationDialog}
            handleExportChatHistory={handleExportChatHistoryFromHook}
            handleClose={handleClose}
            messages={messages}
            showLoader={showLoader}
            canExport={canExport}
          />
          <StyledBodyContainer
            onScroll={debouncedHandleScroll}
            ref={chatBodyScrollContainerRef}
            data-testid='chat-scroll-body-container'
          >
            <WarningBanner
              messages={messages}
              hiddenWarningBanners={hiddenWarningBanners}
              handleBannerCloseFromHook={handleBannerCloseFromHook}
            />
            <MessageList
              prismProgram={prismProgram}
              messages={messages}
              isDragging={isDragging}
              isRequestActive={isRequestActive}
              isInitialized={isInitialized}
              getUserAdditionalProps={getUserAdditionalProps}
              handlePromptClick={handlePromptClick}
              handleUserMessageKeyDown={handleUserMessageKeyDown}
              getBotAdditionalProps={getBotAdditionalProps}
              canExport={canExport}
              handleChartDownloadMenuOpen={handleChartDownloadMenuOpenFromHook}
              handleToggle={handleToggle}
              csvFileRef={csvFileRef}
              canCopy={canCopy}
              handleCopy={handleCopy}
              handleDownloadMenuOpen={handleDownloadMenuOpenFromHook}
              getAIDisclaimerText={getAIDisclaimerText}
            />
          </StyledBodyContainer>
          <ChatInput
            showFullScreen={showFullScreen}
            showDragbar={showDragbar}
            chatFooterContainerRef={chatFooterContainerRef}
            messageInputRef={messageInputRef}
            setDisableSendButton={setDisableSendButton}
            handleKeydown={handleKeydown}
            isRequestActive={isRequestActive}
            handleCancelRequest={handleCancelRequest}
            disableSendButton={disableSendButton}
            showLoader={showLoader}
            sendButtonRef={sendButtonRef}
            handleSend={handleSend}
          />

          <DownloadMenus
            canExport={canExport}
            chartDownloadMenuAnchor={chartDownloadMenuAnchor}
            handleChartDownloadMenuClose={handleChartDownloadMenuCloseFromHook}
            messages={messages}
            selectedChartMessageId={selectedChartMessageId}
            handleChartDownloadSelection={handleChartDownloadSelectionFromHook}
            downloadMenuAnchor={downloadMenuAnchor}
            handleDownloadMenuClose={handleDownloadMenuCloseFromHook}
            selectedDownloadMessageId={selectedDownloadMessageId}
            handleDownloadSelection={handleDownloadSelectionFromHook}
          />
        </StyledChatWindow>
      </StyledChatWindowSubContainer>
      <Snackbar
        open={showCopyToClipboard}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        onClose={() => setShowCopyToClipboard(false)}
        autoHideDuration={1500}
      >
        <Alert onClose={handleClose} severity='success'>
          Copied to clipboard!
        </Alert>
      </Snackbar>
      <ConfirmationDialog
        showDialog={showConfirmationDialog}
        handleConfirm={() => {
          setShowConfirmationDialog(false)
          handleClearHistoryFromHook()
        }}
        handleClose={() => {
          setShowConfirmationDialog(false)
        }}
      />
    </StyledChatWindowContainer>
  )
}

export default ChatWindow

ChatWindow.propTypes = {
  handleClose: PropTypes.func,
  showChatWindow: PropTypes.bool,
  permission: PropTypes.object,
  admin: PropTypes.bool
}

/* Current Function Descriptions:

  CORE COMPONENT FUNCTIONS:
  - handleToggle: Manages visibility toggles for message elements (source code toggle).
  - handleComponentCleanup: Cleanup function that clears timeouts and resets menu anchor states during component unmount.
  - handleKeydown: Handles Enter key press in message input to trigger message submission (with shift-key bypass detection).

  ACCESSIBILITY & UI HELPER FUNCTIONS:
  - getAIDisclaimerText: Retrieves and formats AI disclaimer text that appears below bot responses.
  - getUserAdditionalProps: Returns accessibility props (ARIA labels, button role) for user message elements including prompt detection.
  - getBotAdditionalProps: Returns accessibility props (ARIA labels) for bot messages with smart chart title detection and fallback to response text.

  HOOK-BASED FUNCTION ALIASES:
  - handleSend: Alias for handleSendFromHook - processes user input, validates, adds user message, clears input, and submits query.
  - handleCancelRequest: Alias for handleCancelRequestFromHook - cancels active streaming requests and updates UI state.
  - handlePromptClick: Alias for handlePromptClickFromHook - processes guiding prompt clicks, removes prompts, and initiates queries.
  - handleCopy: Alias for handleCopyFromHook - copies text to clipboard and shows success notification.
  - handleUserMessageKeyDown: Alias for handleUserMessageKeyDownFromHook - handles keyboard navigation for user messages.

  EXTRACTED TO CUSTOM HOOKS:

  useLayoutUtilities Hook:
  - resetSizes: Resets chat window dimensions and adjusts container width for responsive design.
  - adjustChatBodyMaxHeight: Dynamically adjusts chat body max height based on footer height and viewport.
  - debouncedHandleScroll: Debounced scroll handler for performance optimization during rapid scroll events.
  - clearAllTimeouts: Cleanup utility that clears all timeout references to prevent memory leaks.

  useMessageHandlers Hook:
  - clearMessageInput: Clears message input field and disables send button for clean state.
  - handleAutoScrollToBottom: Scrolls conversation to bottom with configurable timing and smooth behavior.
  - handleExpandShrink: Toggles between fullscreen and normal window modes with layout adjustments.
  - handleBannerClose: Adds warning banner categories to hidden list and persists user preferences.
  - handleAutoScrollToMessage: Scrolls to specific message element with smooth animation and error handling.

  useEventHandlers Hook:
  - handleQuerySubmit: Main query submission handler with session initialization, streaming support, and comprehensive error handling.

  useSessionManagement Hook:
  - initializeSession: Creates or restores user sessions with backend API and handles authentication state.
  - populateGuidingPromptsIfReq: Adds default guiding prompts when chat history is empty.
  - handleClearHistory: Clears all chat messages, creates new session, and resets conversation state.

  useExportHandlers Hook:
  - handleExportChatHistory: Exports complete chat conversation as PDF with proper formatting and branding.
  - handleChartDownloadMenuOpen/Close: Manages chart-specific download menu state and positioning.
  - handleChartDownloadSelection: Processes chart export requests (PNG/CSV) with proper data extraction.
  - handleDownloadMenuOpen/Close: Manages general download menu state for SQL results and data exports.
  - handleDownloadSelection: Processes general download requests with CSV generation and file naming.

  useRequestManager Hook:
  - showChatLoader: Displays loading indicators during active API requests.
  - hideChatLoader: Hides loading indicators when requests complete or are cancelled.
  - populateErrorResponse: Adds formatted error messages to conversation with proper styling.
  - handleCancelRequest: Cancels streaming requests, cleans up state, and handles interruption scenarios.

  useMessageProcessor Hook:
  - handlePromptClick: Processes guiding prompt selection, removes prompts from view, and initiates query submission.
  - handleSend: Validates and processes user message submission with input sanitization and analytics.
  - handleCopy: Copies text content to system clipboard with user feedback notification.
  - handleUserMessageKeyDown: Handles keyboard accessibility for message interactions (Enter/Space activation).

  EXTRACTED TO SEPARATE COMPONENTS:

  ChatHeader Component:
  - Window title display with beta branding and program-specific messaging.
  - Expand/shrink toggle with fullscreen mode detection and tooltip management.
  - Clear history button with confirmation dialog integration.
  - Export chat history button with permission-based visibility.
  - Close window button with proper cleanup handling.

  WarningBanner Component:
  - Storage limit warning display with dismissal functionality.
  - User preference persistence for hidden banner categories.
  - Dynamic visibility based on message state and user acknowledgments.

  ChatInput Component:
  - Message input field with multi-line support and character validation.
  - Info banner toggle for additional user guidance.
  - Send/Stop button with dynamic state based on request activity.
  - Keyboard shortcut handling (Enter to send, Shift+Enter for new line).

  DownloadMenus Component:
  - Chart download context menu with PNG/CSV options and data validation.
  - General download context menu for SQL results with smart content detection.
  - Permission-based menu item visibility and proper anchor positioning.

  MessageList Component:
  - Complete message rendering pipeline for user and bot messages.
  - Chart rendering with Plotly integration and error boundary handling.
  - Source code toggle functionality with syntax highlighting.
  - SQL query display with expandable sections and copy functionality.
  - Explanation and sources sections with dynamic content loading.
  - Streaming message support with real-time updates and completion detection.

  STATE MANAGEMENT:
  - useChatState: Consolidated state management for UI, chat, session, menu, and notification states.
  - usePermissions: Permission checks for export and copy functionality based on user role and configuration.
  - Persistent session storage integration for messages, user preferences, and session continuity.
  - Real-time state synchronization between hooks and components for consistent user experience.
*/
